import React from "react";
import GraphSkeleton from "../../../components/ui/graph-skeleton";
import {ScatterChart, Scatter, CartesianGrid, XAxis, YAxis, Tooltip, Legend, ResponsiveContainer, Label} from "recharts";

interface LhcRouteGraphProps {
    data: Record<string, any>[];
    /** Row field used to color the points (e.g. "vendor_type", "branch_name") */
    colorBy: string;
    /** Human label for the colorBy field, shown in the tooltip / legend */
    colorByLabel: string;
    loading?: boolean;
}

// Distinct, legible palette; "Unknown" always renders in muted slate.
const COLOR_PALETTE = [
    "#2563eb", "#f59e0b", "#10b981", "#ef4444", "#8b5cf6",
    "#06b6d4", "#ec4899", "#14b8a6", "#f97316", "#6366f1",
];
const UNKNOWN_COLOR = "#94a3b8";
const UNKNOWN_LABEL = "Unknown";
// "Outliers" Color-By mode: normal points blue, outliers (>±10% of median) red.
export const OUTLIERS_COLOR_BY = "__outliers__";
const NORMAL_COLOR = "#2563eb";
const OUTLIER_COLOR = "#dc2626";
const NORMAL_LABEL = "Normal";
const OUTLIER_LABEL = "Outlier";

const CustomTooltip = ({ active, payload, colorByLabel }: any) => {
    if (active && payload?.length) {
        const { display_date, lorry_hire_expenses, count, colorValue, is_outlier } = payload[0].payload;
        return (
            <div className="bg-white border border-gray-200 rounded-lg shadow-md px-3 py-2 text-sm">
                <p className="font-medium text-gray-800 mb-1">
                    Date: <span className="font-semibold">{display_date}</span>
                </p>
                <p className="text-blue-600 font-medium mb-1">
                    Lorry Hire Expenses:{" "}
                    <span className="font-semibold">
                        ₹{lorry_hire_expenses.toLocaleString("en-IN")}
                    </span>
                </p>
                <p className="text-gray-700 mb-1">
                    {colorByLabel}: <span className="font-semibold">{colorValue}</span>
                </p>
                <p className="text-gray-600">
                    Count: <span className="font-semibold">{count}</span>
                </p>
                {is_outlier && (
                    <p className="mt-1 font-semibold text-red-600">⚠ Outlier (&gt; ±10% of median)</p>
                )}
            </div>
        );
    }
    return null;
};

const LhcRouteGraph: React.FC<LhcRouteGraphProps> = ({ data, colorBy, colorByLabel, loading }) => {
    if (loading) return <GraphSkeleton loading />;
    if (!data?.length) return <GraphSkeleton />;

    const isOutlierMode = colorBy === OUTLIERS_COLOR_BY;

    const formattedData = data
        .map((item) => {
            const date = item.lhc_date ? new Date(item.lhc_date) : null;
            const display_date = date
                ? date.toLocaleDateString("en-IN", { day: "2-digit", month: "short" })
                : "-";
            const raw = item[colorBy];
            const colorValue = isOutlierMode
                ? (item.is_outlier ? OUTLIER_LABEL : NORMAL_LABEL)
                : raw === null || raw === undefined || String(raw).trim() === ""
                    ? UNKNOWN_LABEL
                    : String(raw);
            return {
                display_date,
                raw_date: date,
                lorry_hire_expenses: Number(item.lorry_hire_expenses) || 0,
                colorValue,
                is_outlier: !!item.is_outlier,
            };
        })
        .filter((item) => !!item.raw_date);

    const grouped = formattedData.reduce<Record<string, any[]>>((acc, item) => {
        if (!acc[item.display_date]) acc[item.display_date] = [];
        acc[item.display_date].push(item);
        return acc;
    }, {});

    const sortedDates = Object.keys(grouped).sort(
        (a, b) => grouped[a][0].raw_date.getTime() - grouped[b][0].raw_date.getTime()
    );

    const dateToXIndex = Object.fromEntries(sortedDates.map((d, i) => [d, i + 1]));

    // Collapse identical (date, expense, category, outlier) points and keep a count.
    const scatterData = sortedDates.flatMap((date) => {
        const byKey = grouped[date].reduce<Record<string, any>>((acc, item) => {
            const key = `${item.lorry_hire_expenses}__${item.colorValue}__${item.is_outlier ? 1 : 0}`;
            if (!acc[key]) acc[key] = { ...item, count: 1 };
            else acc[key].count += 1;
            return acc;
        }, {});
        return Object.values(byKey).map((it) => ({
            xIndex: dateToXIndex[date],
            display_date: date,
            lorry_hire_expenses: it.lorry_hire_expenses,
            colorValue: it.colorValue,
            is_outlier: it.is_outlier,
            count: it.count,
        }));
    });

    // Stable color assignment per distinct value. In "Outliers" mode the colorValue
    // is Normal/Outlier and we draw Outlier last (on top); otherwise "Unknown" sorts last.
    const distinctValues = Array.from(new Set(scatterData.map((d) => d.colorValue))).sort((a, b) => {
        if (isOutlierMode) {
            if (a === OUTLIER_LABEL) return 1;
            if (b === OUTLIER_LABEL) return -1;
            return 0;
        }
        if (a === UNKNOWN_LABEL) return 1;
        if (b === UNKNOWN_LABEL) return -1;
        return a.localeCompare(b);
    });

    const colorMap: Record<string, string> = {};
    let paletteIdx = 0;
    distinctValues.forEach((v) => {
        if (isOutlierMode) {
            colorMap[v] = v === OUTLIER_LABEL ? OUTLIER_COLOR : NORMAL_COLOR;
        } else {
            colorMap[v] = v === UNKNOWN_LABEL ? UNKNOWN_COLOR : COLOR_PALETTE[paletteIdx++ % COLOR_PALETTE.length];
        }
    });

    const series = distinctValues.map((v) => ({
        value: v,
        color: colorMap[v],
        points: scatterData.filter((d) => d.colorValue === v),
    }));

    const totalDates = sortedDates.length;
    const targetTicks = Math.min(35, Math.max(25, totalDates));
    const step = Math.ceil(totalDates / targetTicks);
    const ticks = sortedDates
        .map((_, i) => (i % step === 0 ? i + 1 : null))
        .filter(Boolean) as number[];

    return (
        <div className="w-full rounded-xl border bg-card shadow-sm">
            <div className="px-4 pt-3 text-sm font-medium text-slate-800">
                Lorry Hire Expenses over time
            </div>
            <div className="w-full h-[480px] md:h-[450px] sm:h-[360px] pt-1">
            <ResponsiveContainer width="100%" height="100%">
                <ScatterChart margin={{ top: 5, right: 8, bottom: 5, left: 15 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />

                    <XAxis type="number" dataKey="xIndex" domain={[1, totalDates]} ticks={ticks} tickFormatter={(idx) => sortedDates[idx - 1] || ""} tick={{ fontSize: 12 }} height={60} angle={-30} textAnchor="end" >
                        <Label
                            value="LHC Date"
                            offset={2}
                            position="insideBottom"
                            style={{ fontSize: 13, fill: "#374151" }}
                        />
                    </XAxis>

                    <YAxis type="number" dataKey="lorry_hire_expenses" tick={{ fontSize: 12 }} tickFormatter={(val) => `₹${val.toLocaleString("en-IN")}`} >
                        <Label
                            value="Lorry Hire Expenses (₹)"
                            angle={-90}
                            offset={-5}
                            position="insideLeft"
                            style={{ textAnchor: "middle", fontSize: 14, fill: "#374151" }}
                        />
                    </YAxis>

                    <Tooltip content={<CustomTooltip colorByLabel={colorByLabel} />} cursor={{ strokeDasharray: "3 3" }} />

                    <Legend wrapperStyle={{ fontSize: 12 }} />

                    {series.map((s) => (
                        <Scatter key={s.value} name={s.value} data={s.points} fill={s.color} opacity={0.85} shape="circle" />
                    ))}
                </ScatterChart>
            </ResponsiveContainer>
            </div>
        </div>
    );
};

export default LhcRouteGraph;
